import React, { useState, useEffect, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import { formatInTenantTz } from "../../utils/dateUtils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  ORDERS_ENDPOINT,
  ORDERS_STATUS_EDIT_ENDPOINT,
  OASIS_ORDERS_EXPORT_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import AppDatePicker from "../../common/app-date-picker";
import OrderSearchInput from "./OrderSearchInput";
import AppDropdown from "../../common/app-dropdown";
import { grayFontColor } from "../../cssUtils/constants";
import { DatePicker } from "@mui/x-date-pickers";
import typography from "../../cssUtils/typography";

const ORDER_STATUS_OPTIONS = [
  "created",
  "pending",
  "confirm",
  "completed",
  "incomplete",
  "cancelled",
  "reject",
];

const statusChip = (status) => {
  if (status === "completed")
    return (
      <Chip variant="outlined" color="success" label="Completed" size="small" />
    );
  if (status === "confirm")
    return (
      <Chip variant="outlined" color="primary" label="Confirmed" size="small" />
    );
  if (status === "incomplete")
    return (
      <Chip
        variant="outlined"
        color="default"
        label="Incomplete"
        size="small"
      />
    );
  if (status === "cancelled")
    return (
      <Chip variant="outlined" color="error" label="Cancelled" size="small" />
    );
  if (status === "reject")
    return (
      <Chip variant="outlined" color="error" label="Rejected" size="small" />
    );
  if (status === "created")
    return (
      <Chip variant="outlined" color="info" label="Created" size="small" />
    );
  return (
    <Chip variant="outlined" color="warning" label="Pending" size="small" />
  );
};

function OrdersTable({
  refreshKey,
  searchInput,
  setQuery,
  setShowInput,
  setSearchValue,
  showInput,
  query,
  setRefreshKey,
}) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [rowCount, setRowCount] = useState(0);
  const [ordersData, setOrdersData] = useState([]);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [dateRange, setDateRange] = useState([null, null]);
  const [appliedDateRange, setAppliedDateRange] = useState([null, null]);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [dateError, setDateError] = useState("");
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Operational filters (BB-2144): Zone, Status, Delivery Slot, Request Date.
  // Raw dropdown state vs. "applied" state mirrors the existing dateRange /
  // appliedDateRange pattern so filters only take effect on Search click.
  const [zoneFilter, setZoneFilter] = useState("");
  const [appliedZone, setAppliedZone] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [deliverySlotFilter, setDeliverySlotFilter] = useState("");
  const [appliedDeliverySlot, setAppliedDeliverySlot] = useState("");
  const [requestDateFilter, setRequestDateFilter] = useState(null);
  const [appliedRequestDate, setAppliedRequestDate] = useState(null);

  const handleZoneChange = (value) => {
    setZoneFilter(value);
    setAppliedZone(value);
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setAppliedStatus(value);
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleDeliverySlotChange = (value) => {
    setDeliverySlotFilter(value);
    setAppliedDeliverySlot(value);
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleRequestDateChange = (newValue) => {
    setRequestDateFilter(newValue);
    setAppliedRequestDate(newValue);
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const [openModal, setOpenModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [availableStatusOptions, setAvailableStatusOptions] = useState([]);

  // Derived from currently loaded order data (no dedicated API exists for these yet)
  const zoneOptions = useMemo(
    () =>
      Array.from(
        new Set(ordersData.map((o) => o.zone_name).filter(Boolean)),
      ),
    [ordersData],
  );
  const deliverySlotOptions = useMemo(
    () =>
      Array.from(
        new Set(ordersData.map((o) => o.time_slot).filter(Boolean)),
      ),
    [ordersData],
  );

  const handleToDateOpen = () => {
    if (!dateRange[0]) {
      setDateError("Please select Start Date first");
      return;
    }
    setDateError("");
    setEndDatePickerOpen(true);
  };

  const handleDateRangeApply = () => {
    setAppliedDateRange(dateRange);
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleDateRangeCancel = () => {
    setDateRange([null, null]);
    setAppliedDateRange([null, null]);
    setDateError("");
    setSearchTriggered(true);
    setController((p) => ({ ...p, page: 0 }));
  };

  const fetchOrders = async (searchURL = "", isTriggered = false) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    let offset = controller.page * controller.rowsPerPage;
    if (isTriggered) offset = 0;

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-tenant-ID": tenant,
      };
      const response = await fetch(
        `${ORDERS_ENDPOINT}?limit=${controller.rowsPerPage}&offset=${offset}${searchURL}`,
        { headers },
      );
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === "user_inactive") {
          auth.logOut();
          navigate("/login");
        }
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();
      setOrdersData(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const buildSearchURL = () => {
    let url = "";
     if (searchInput) url += `&search_id=${encodeURIComponent(searchInput)}`;
    if (appliedZone) url += `&zone=${encodeURIComponent(appliedZone)}`;
    if (appliedStatus) url += `&status=${encodeURIComponent(appliedStatus)}`;
    if (appliedDeliverySlot)
      url += `&delivery_slot=${encodeURIComponent(appliedDeliverySlot)}`;
    if (appliedRequestDate)
      url += `&request_date=${appliedRequestDate.format("YYYY-MM-DD")}`;
    if (appliedDateRange[0])
      url += `&start_date=${appliedDateRange[0].format("YYYY-MM-DD")}`;
    if (appliedDateRange[1])
      url += `&end_date=${appliedDateRange[1].format("YYYY-MM-DD")}`;
    return url;
  };

  const handleExport = async () => {
    // Same filter params as the table fetch, without limit/offset
    const searchURL = buildSearchURL().replace(/^&/, "?");
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    setIsExporting(true);
    try {
      const response = await fetch(`${OASIS_ORDERS_EXPORT_ENDPOINT}${searchURL}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-tenant-ID": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;
      a.download = `order-requests-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      alert("Failed to export order requests. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!searchTriggered && !searchInput) fetchOrders();
  }, [controller, refreshKey]);

  useEffect(() => {
    const shouldFetch =
      searchTriggered ||
      Boolean(searchInput) ||
      Boolean(appliedZone) ||
      Boolean(appliedStatus) ||
      Boolean(appliedDeliverySlot) ||
      Boolean(appliedRequestDate) ||
      Boolean(appliedDateRange[0]) ||
      Boolean(appliedDateRange[1]);

    if (shouldFetch) {
      const url = buildSearchURL();
      setController((prev) => ({ ...prev, page: 0 }));
      fetchOrders(url, true);
    }
  }, [
    searchTriggered,
    searchInput,
    appliedDateRange,
    appliedZone,
    appliedStatus,
    appliedDeliverySlot,
    appliedRequestDate,
  ]);

  const handleOpenModal = (order) => {
    setSelectedAddress(order.address);
    setOpenModal(true);
  };

  const handleOpenStatusModal = (order) => {
    setCurrentOrder(order);
    setNewStatus(order.status);
    setOpenStatusModal(true);
    const options = {
      created: ["pending", "confirm", "completed", "cancelled"],
      pending: ["confirm", "completed", "cancelled"],
      confirm: ["completed", "cancelled"],
    };
    setAvailableStatusOptions(options[order.status] ?? []);
  };

  const handleCloseStatusModal = () => {
    setOpenStatusModal(false);
    setCurrentOrder(null);
    setNewStatus("");
  };

  const handleSaveStatus = async () => {
    if (!currentOrder) return;
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    try {
      const response = await fetch(
        `${ORDERS_STATUS_EDIT_ENDPOINT}${currentOrder.code}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-tenant-ID": tenant,
          },
          body: JSON.stringify({ status: newStatus }),
        },
      );
      if (!response.ok) throw new Error("Failed to update status");
      setOrdersData((prev) =>
        prev.map((o) =>
          o.code === currentOrder.code ? { ...o, status: newStatus } : o,
        ),
      );
      handleCloseStatusModal();
    } catch (error) {
      console.error("Error updating status:", error.message);
    }
  };

  const columns = [
    { id: "code", label: "Order Code" },
    { id: "customer_code", label: "Customer Code" },
    { id: "customer_name", label: "Customer Name" },
    { id: "driver_name", label: "Driver Name" },
    {
      id: "zone_name",
      label: "Zone",
      render: (o) => (
        <ButtonOne
          variant="text"
          color="primary"
          onClick={() => handleOpenModal(o)}
        >
          {o.zone_name}
        </ButtonOne>
      ),
    },
    { id: "time_slot", label: "Time Slot" },
    {
      id: "status",
      label: "Status",
      render: (o) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {statusChip(o.status)}
          {["created", "pending", "confirm"].includes(o.status) && (
            <IconButton
              size="small"
              onClick={() => handleOpenStatusModal(o)}
              sx={{ ml: 1 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
    { id: "party_size", label: "Party Size" },
    {
      id: "created_at",
      label: "Created At",
      render: (o) =>
        o.created_at ? formatInTenantTz(o.created_at, "DD/MM/YYYY") : "--",
    },
    {
      id: "delivery_date",
      label: "Request Delivery Date",
      render: (o) =>
        o.delivery_date ? dayjs(o.delivery_date).format("DD/MM/YYYY") : "--",
    },
    {
      id: "order_delivered_date",
      label: "Order Completed Date",
      render: (o) =>
        o.order_delivered_date
          ? dayjs(o.order_delivered_date).format("DD/MM/YYYY")
          : "--",
    },
  ];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Search input width increased so placeholder is fully visible */}
        <Box sx={{ minWidth: 450 }}>
          <OrderSearchInput
            setSearchValue={setSearchValue}
            updateRefreshKey={setRefreshKey}
            query={query}
            setQuery={setQuery}
            showInput={showInput}
            setShowInput={setShowInput}
          />
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AppDropdown
            endIcon={
              <KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />
            }
            paperProps={{ maxWidth: "500px" }}
            menuContent={(closeMenu) => (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Typography sx={{ ...typography.bodyLarge }}>
                    Select date range:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <DatePicker
                      disableFuture
                      label="Start Date"
                      format="DD/MM/YYYY"
                      value={dateRange[0]}
                      onChange={(newValue) =>
                        setDateRange([newValue, dateRange[1]])
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: "200px",
                            "& .MuiInputBase-root": { height: "40px" },
                          },
                        },
                      }}
                    />
                    <DatePicker
                      disableFuture
                      label="End Date"
                      format="DD/MM/YYYY"
                      value={dateRange[1]}
                      onChange={(newValue) =>
                        setDateRange([dateRange[0], newValue])
                      }
                      minDate={dateRange[0] || undefined}
                      open={endDatePickerOpen}
                      onOpen={handleToDateOpen}
                      onClose={() => setEndDatePickerOpen(false)}
                      slotProps={{
                        textField: {
                          error: (dateRange[1] && !dateRange[0]) || !!dateError,
                          helperText:
                            dateRange[1] && !dateRange[0]
                              ? "Please select Start Date first"
                              : dateError,
                          size: "small",
                          sx: {
                            width: "200px",
                            "& .MuiInputBase-root": { height: "40px" },
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    <ButtonTwo
                      onClick={() => {
                        handleDateRangeCancel();
                        closeMenu();
                      }}
                      variant="outlined"
                    >
                      Cancel
                    </ButtonTwo>
                    <ButtonOne
                      onClick={() => {
                        handleDateRangeApply();
                        closeMenu();
                      }}
                      variant="contained"
                      disabled={!dateRange[0] || !dateRange[1]}
                    >
                      Apply
                    </ButtonOne>
                  </Box>
                </Box>
              </Box>
            )}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}
            >
              <Box
                component="span"
                sx={{
                  mr: appliedDateRange[0] && appliedDateRange[1] ? 1 : 0,
                  flexShrink: 0,
                }}
              >
                {appliedDateRange[0] && appliedDateRange[1]
                ? "Created Date:"
                : "Created Date  "}
              </Box>
              {appliedDateRange[0] && appliedDateRange[1] && (
                <Chip
                  size="small"
                  onDelete={handleDateRangeCancel}
                  label={`${appliedDateRange[0].format("DD/MM/YYYY")} - ${appliedDateRange[1].format("DD/MM/YYYY")}`}
                  sx={{
                    maxWidth: { sm: 80, md: 200 },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                />
              )}
            </Box>
          </AppDropdown>

          {/* Operational filters — BB-2144: Zone, Status, Delivery Slot, Request Date */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="zone-filter-label">Zone</InputLabel>
            <Select
              labelId="zone-filter-label"
              label="Zone"
              value={zoneFilter}
              onChange={(e) => handleZoneChange(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {zoneOptions.map((z) => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {ORDER_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="delivery-slot-filter-label">
              Delivery Slot
            </InputLabel>
            <Select
              labelId="delivery-slot-filter-label"
              label="Delivery Slot"
              value={deliverySlotFilter}
              onChange={(e) => handleDeliverySlotChange(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {deliverySlotOptions.map((slot) => (
                <MenuItem key={slot} value={slot}>
                  {slot}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="Request Date"
            format="DD/MM/YYYY"
            value={requestDateFilter}
            onChange={handleRequestDateChange}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  width: "170px",
                  "& .MuiInputBase-root": { height: "40px" },
                },
              },
            }}
          />
        </LocalizationProvider>

        {/* Search and Reset buttons — kept from original */}
        <ButtonOne
          variant="contained"
          size="large"
          onClick={() => {
            setAppliedZone(zoneFilter);
            setAppliedStatus(statusFilter);
            setAppliedDeliverySlot(deliverySlotFilter);
            setAppliedRequestDate(requestDateFilter);
            setSearchTriggered(true);
            setController((p) => ({ ...p, page: 0 }));
          }}
        >
          Search
        </ButtonOne>
        <ButtonTwo
          variant="outlined"
          size="large"
          onClick={() => {
            setDateRange([null, null]);
            setAppliedDateRange([null, null]);
            setDateError("");
            setZoneFilter("");
            setAppliedZone("");
            setStatusFilter("");
            setAppliedStatus("");
            setDeliverySlotFilter("");
            setAppliedDeliverySlot("");
            setRequestDateFilter(null);
            setAppliedRequestDate(null);
            setSearchValue("");
            setQuery("");
            setShowInput(false);
            setController((p) => ({ ...p, page: 0 }));
            setSearchTriggered(true);
          }}
        >
          Reset
        </ButtonTwo>
        <ButtonTwo
          variant="outlined"
          size="large"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export CSV"}
        </ButtonTwo>

        <Tooltip
          title="This filter applies to order requests created within the selected start and end dates."
          arrow
        >
          <IconButton>
            <InfoIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>

      <AppTable
        columns={columns}
        rows={ordersData}
        rowKey={(o) => o.code}
        emptyMessage="No orders found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) =>
          setController((p) => ({ ...p, page: newPage }))
        }
        onRowsPerPageChange={(e) =>
          setController((p) => ({
            ...p,
            rowsPerPage: parseInt(e.target.value, 10),
            page: 0,
          }))
        }
      />

      {/* Address Details Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Address Details</DialogTitle>
        <DialogContent dividers>
          {selectedAddress ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body1">
                <strong>Address Name:</strong> {selectedAddress.name || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Latitude:</strong> {selectedAddress.latitude || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Longitude:</strong> {selectedAddress.longitude || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Zone:</strong> {selectedAddress.zone?.name || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Subzone:</strong>{" "}
                {selectedAddress.subzone?.name || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Appartment Details:</strong>{" "}
                {selectedAddress.appartment_details || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Building Details:</strong>{" "}
                {selectedAddress.building_details || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Street Details:</strong>{" "}
                {selectedAddress.street_details || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Landmark Details:</strong>{" "}
                {selectedAddress.landmark_details || "--"}
              </Typography>
              <Typography variant="body1">
                <strong>Full Address:</strong>{" "}
                {selectedAddress.full_address || "--"}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1">
              No address details available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonOne onClick={() => setOpenModal(false)}>Close</ButtonOne>
        </DialogActions>
      </Dialog>

      {/* Status Edit Modal */}
      <Dialog
        open={openStatusModal}
        onClose={handleCloseStatusModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Order Status</DialogTitle>
        <DialogContent dividers>
          {currentOrder ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <Typography variant="body1">
                <strong>Order Code:</strong> {currentOrder.code}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong>{" "}
                {currentOrder.status.charAt(0).toUpperCase() +
                  currentOrder.status.slice(1)}
              </Typography>
              <Typography variant="body1">
                <strong>Status Change To:</strong>
              </Typography>
              {currentOrder.status === "completed" ||
              currentOrder.status === "cancelled" ? (
                <Typography variant="body1">
                  <strong>Status:</strong>{" "}
                  {currentOrder.status.charAt(0).toUpperCase() +
                    currentOrder.status.slice(1)}
                </Typography>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={newStatus}
                    label="Status"
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {availableStatusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          ) : (
            <Typography variant="body1">No order selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonTwo onClick={handleCloseStatusModal}>Cancel</ButtonTwo>
          {currentOrder &&
            !["completed", "cancelled"].includes(currentOrder.status) && (
              <ButtonOne
                onClick={handleSaveStatus}
                variant="contained"
                color="primary"
              >
                Save
              </ButtonOne>
            )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default OrdersTable;